import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LinesStackSkeleton } from "@/components/skeletons";
import AzureDirectoryInstruction from "@/components/modals/AzureDirectoryInstruction";
import SAMLInstructions from "@/components/cards/SAMLInstructions";
import { fetchAllConnector, tokenForConsent, updateConnector } from "@/services/setting.service";
import { AZURE_DIRECTORY_FORM, AZURE_OAUTH_OPTIONS, CONNECTOR_TYPES } from "@/utils/constants/appConstants/DirectoryConstants";
import { BUTTONS, LABELS, OIDC_INSTRUCTION, TEXT } from "@/utils/constants/appConstants/commonConstants";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import { getUserId } from "@/utils/functions/TokenFunction";
import { errorToasterMessage } from "@/utils/functions/toaster";

const AzureCredentialsForm = () => {
    const [formData, setFormData] = useState<any>({ tenant_id: "" });
    const [loading, setLoading] = useState(false);
    const [errMsg, setErrMsg] = useState<any>({});
    const [consentGiven, setConsentGiven] = useState(false);
    const [oidcConnectorData, setOidcConnectorData] = useState<any>();
    const [samlConnectorData, setSamlConnectorData] = useState<any>();
    const [activateLoader, setActivateLoader] = useState(false);
    const [openInstructionModal, setOpenInstructionModal] = useState(false);
    const [selectOAuthMethod, setSelectOAuthMethod] = useState<string>("");
    const navigate = useNavigate();

    useEffect(() => {
        fetchOCIDConnectorData();
        fetchSAMLConnectorData();
    }, []);

    const fetchOCIDConnectorData = () => {
        setLoading(true);
        fetchAllConnector(
            { type: CONNECTOR_TYPES.OIDC },
            (res: any) => {
                setLoading(false);
                setOidcConnectorData(null);
                if (res.length > 0) {
                    setOidcConnectorData(res[0]);
                    setFormData({ tenant_id: res[0].data.tenant_id });
                }
            },
            () => setLoading(false)
        );
    };

    const fetchSAMLConnectorData = () => {
        setLoading(true);
        fetchAllConnector(
            { type: CONNECTOR_TYPES.SAML },
            (res: any) => {
                setLoading(false);
                setSamlConnectorData(null);
                if (res.length > 0) setSamlConnectorData(res[0]);
            },
            () => setLoading(false)
        );
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setErrMsg({ tenant_id: "" });
        setFormData((prevData: any) => ({ ...prevData, [name]: value }));
    };

    const handleActivation = (id: string, checked: boolean) => {
        setActivateLoader(true);
        updateConnector(
            id,
            { is_active: checked },
            (res: any) => {
                setActivateLoader(false);
                window.open(res.redirect_url, "_blank");
                setSelectOAuthMethod("");
                fetchOCIDConnectorData();
                fetchSAMLConnectorData();
            },
            () => setActivateLoader(false)
        );
    };

    useEffect(() => {
        if (oidcConnectorData) {
            setSelectOAuthMethod(CONNECTOR_TYPES.OIDC);
        } else if (samlConnectorData) {
            setSelectOAuthMethod(CONNECTOR_TYPES.SAML);
        }
    }, [oidcConnectorData, samlConnectorData]);

    const renderOidcButton = () =>
        oidcConnectorData?.is_active ? (
            <Button
                type="button"
                variant="destructive"
                className="gs-directory-action-btn"
                disabled={activateLoader}
                onClick={() => handleActivation(oidcConnectorData.id, false)}
            >
                {activateLoader && <Spinner size="sm" className="mr-2" />}
                {AZURE_DIRECTORY_FORM.deactivate_access}
            </Button>
        ) : (
            <Button
                type="button"
                className="gs-directory-action-btn"
                disabled={!formData.tenant_id || consentGiven}
                onClick={() => {
                    setConsentGiven(true);
                    tokenForConsent(
                        { organization: getCurrentOrganization().organization, user: getUserId(), tenant_id: formData.tenant_id },
                        (res: any) => {
                            setConsentGiven(false);
                            navigate(`/consent?domain=${formData.tenant_id}&token=${res}`);
                        },
                        (err: any) => errorToasterMessage(err?.response?.data?.message)
                    );
                }}
            >
                {consentGiven && <Spinner size="sm" className="mr-2" />}
                {BUTTONS.GRANT_ACCESS}
            </Button>
        );

    if (loading) {
        return <LinesStackSkeleton rowCount={4} className="gs-directory-card gs-directory-card__section" />;
    }

    return (
        <div>
            <div className="gs-directory-oauth-select">
                <Select
                    value={selectOAuthMethod}
                    onValueChange={setSelectOAuthMethod}
                    disabled={!!oidcConnectorData || !!samlConnectorData}
                >
                    <SelectTrigger
                        id="azure-oauth-method-select"
                        className="gs-directory-oauth-select__trigger w-full"
                    >
                        <SelectValue placeholder="Select your OAUTH method" />
                    </SelectTrigger>
                    <SelectContent
                        position="popper"
                        align="start"
                        sideOffset={6}
                        className="gs-directory-oauth-select__content"
                    >
                        {AZURE_OAUTH_OPTIONS.map((opt: any) => (
                            <SelectItem
                                key={opt.value}
                                value={opt.value}
                                className="gs-directory-oauth-select__item"
                            >
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {(selectOAuthMethod === CONNECTOR_TYPES.OIDC || oidcConnectorData?.type === CONNECTOR_TYPES.OIDC) && (
                <div className="gs-directory-card">
                    <div className="gs-directory-card__section gs-directory-card__section--bordered">
                        <div className="gs-directory-field">
                            <label className="gs-directory-field__label" htmlFor="azure-tenant-id-input">
                                {AZURE_DIRECTORY_FORM.tenantId}
                                {oidcConnectorData?.is_active && (
                                    <span className="gs-directory-field__label-status">{LABELS.CONNECTED}</span>
                                )}
                            </label>
                            <div className="gs-directory-field__row">
                                <Input
                                    id="azure-tenant-id-input"
                                    required
                                    type="text"
                                    placeholder={LABELS.TENANT_ID}
                                    name="tenant_id"
                                    value={formData.tenant_id}
                                    onChange={handleChange}
                                    disabled={!!oidcConnectorData?.data?.tenant_id}
                                    className="gs-directory-field__input"
                                />
                                {!oidcConnectorData?.data?.tenant_id && (
                                    <em
                                        className="icon ni ni-help gs-directory-field__help"
                                        onClick={() => setOpenInstructionModal(true)}
                                    />
                                )}
                            </div>
                            {errMsg.tenant_id && <p className="gs-directory-field__error">{errMsg.tenant_id}</p>}
                        </div>
                        {renderOidcButton()}
                    </div>
                    <div className="gs-directory-card__section">
                        <h5 className="gs-directory-section__title" id="saml-instruction-heading">
                            {TEXT.OIDC_INSTRUCTION_HEADING}
                        </h5>
                        <ol className="gs-directory-steps">
                            <li className="gs-directory-steps__item" id="saml-instruction-1" dangerouslySetInnerHTML={{ __html: OIDC_INSTRUCTION.step1 }} />
                            {[2, 3, 4, 5, 6, 7].map((n) => (
                                <li key={n} className="gs-directory-steps__item" id={`oidc-instruction-${n}`}>
                                    {(OIDC_INSTRUCTION as any)[`step${n}`]}
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            )}

            <AzureDirectoryInstruction show={openInstructionModal} onHide={() => setOpenInstructionModal(false)} />
            {(selectOAuthMethod === CONNECTOR_TYPES.SAML || samlConnectorData?.type === CONNECTOR_TYPES.SAML) && (
                <div className="gs-directory-card">
                    <SAMLInstructions connector={samlConnectorData} handleActivation={handleActivation} loader={loading} />
                </div>
            )}
        </div>
    );
};

export default AzureCredentialsForm;
